import { LEGAL_VERSIONS } from '../constants/version';
import {
  LegalDocument,
  LegalDocumentType,
  LegalParagraph,
  LegalSection,
} from '../types/legalDocument';

export const privacyPolicy: LegalDocument = {
  id: 'privacy-policy',

  title: 'POLÍTICA DE PRIVACIDADE DA NEXORA',

  version: LEGAL_VERSIONS.privacy,

  source: 'docs/legal/privacy-policy.md',

  documentType: 'privacy' as LegalDocumentType,

  lastUpdated: 'Julho de 2026',

  effectiveDate: 'Julho de 2026',

  sections: [
    {
      id: 'commitment',

      title: 'Compromisso da Nexora',

      paragraphs: [
        {
          id: 'commitment-1',
          text: 'A Nexora acredita que privacidade é um direito fundamental.',
        },

        {
          id: 'commitment-2',
          text: 'Por isso, tratamos os dados pessoais de nossos usuários com responsabilidade, transparência e respeito, adotando medidas técnicas e administrativas para protegê-los durante toda a utilização da plataforma.',
        },

        {
          id: 'commitment-3',
          text: 'Esta Política explica quais informações são coletadas, por que elas são necessárias e como são utilizadas.',
        },

        {
          id: 'commitment-4',
          text: 'Ao utilizar a Nexora, você concorda com as práticas descritas neste documento.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'collected-data',

      title: '1. Dados que Coletamos',

      paragraphs: [
        {
          id: 'collected-data-1',
          text: 'A Nexora coleta apenas as informações necessárias para o funcionamento da plataforma.',
        },

        {
          id: 'collected-data-2',
          text: 'Dependendo da forma de autenticação utilizada, poderão ser coletados:',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'identification-data',

      title: 'Dados de identificação',

      paragraphs: [
        {
          id: 'identification-data-1',
          text: '* Nome',
        },

        {
          id: 'identification-data-2',
          text: '* Endereço de e-mail',
        },

        {
          id: 'identification-data-3',
          text: '* Foto de perfil (quando disponibilizada pelo provedor de autenticação)',
        },

        {
          id: 'identification-data-4',
          text: '* Identificador único da conta (UID)',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'account-data',

      title: 'Dados da conta',

      paragraphs: [
        {
          id: 'account-data-1',
          text: '* Data de criação',
        },

        {
          id: 'account-data-2',
          text: '* Último acesso',
        },

        {
          id: 'account-data-3',
          text: '* Status da conta',
        },

        {
          id: 'account-data-4',
          text: '* Informações relacionadas à assinatura',
        },

        {
          id: 'account-data-5',
          text: '* Histórico administrativo da conta',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'technical-data',

      title: 'Dados técnicos',

      paragraphs: [
        {
          id: 'technical-data-1',
          text: 'Durante a utilização da plataforma poderão ser registrados dados técnicos necessários para segurança, auditoria e funcionamento dos serviços.',
        },

        {
          id: 'technical-data-2',
          text: 'Esses registros poderão incluir:',
        },

        {
          id: 'technical-data-3',
          text: '* registros de autenticação;',
        },

        {
          id: 'technical-data-4',
          text: '* data e horário de operações;',
        },

        {
          id: 'technical-data-5',
          text: '* informações relacionadas ao funcionamento da plataforma.',
        },

        {
          id: 'technical-data-6',
          text: 'A Nexora não realiza coleta de informações pessoais desnecessárias.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'data-use',

      title: '2. Como Utilizamos seus Dados',

      paragraphs: [
        {
          id: 'data-use-1',
          text: 'Os dados coletados são utilizados para:',
        },

        {
          id: 'data-use-2',
          text: '* autenticar usuários;',
        },

        {
          id: 'data-use-3',
          text: '* administrar contas;',
        },

        {
          id: 'data-use-4',
          text: '* liberar ou restringir acessos conforme regras da plataforma;',
        },

        {
          id: 'data-use-5',
          text: '* gerenciar assinaturas;',
        },

        {
          id: 'data-use-6',
          text: '* registrar ações administrativas;',
        },

        {
          id: 'data-use-7',
          text: '* melhorar continuamente os serviços;',
        },

        {
          id: 'data-use-8',
          text: '* garantir segurança, auditoria e rastreabilidade.',
        },

        {
          id: 'data-use-9',
          text: 'Jamais utilizaremos seus dados para finalidades incompatíveis com esta Política.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'information-sharing',

      title: '3. Compartilhamento de Informações',

      paragraphs: [
        {
          id: 'information-sharing-1',
          text: 'A Nexora não comercializa dados pessoais.',
        },

        {
          id: 'information-sharing-2',
          text: 'Os dados somente poderão ser compartilhados quando:',
        },

        {
          id: 'information-sharing-3',
          text: '* necessário para prestação dos serviços;',
        },

        {
          id: 'information-sharing-4',
          text: '* exigido por obrigação legal;',
        },

        {
          id: 'information-sharing-5',
          text: '* solicitado por autoridade competente;',
        },

        {
          id: 'information-sharing-6',
          text: '* autorizado pelo próprio usuário.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'data-storage',

      title: '4. Armazenamento dos Dados',

      paragraphs: [
        {
          id: 'data-storage-1',
          text: 'Os dados são armazenados utilizando infraestrutura fornecida por provedores reconhecidos no mercado, incluindo serviços da plataforma Firebase, da Google.',
        },

        {
          id: 'data-storage-2',
          text: 'Esses provedores adotam padrões internacionais de segurança para proteção das informações.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'information-security',

      title: '5. Segurança das Informações',

      paragraphs: [
        {
          id: 'information-security-1',
          text: 'A Nexora adota medidas técnicas e administrativas para reduzir riscos de acesso não autorizado, perda, alteração ou divulgação indevida das informações.',
        },

        {
          id: 'information-security-2',
          text: 'Entre essas medidas estão:',
        },

        {
          id: 'information-security-3',
          text: '* autenticação segura;',
        },

        {
          id: 'information-security-4',
          text: '* controle de permissões;',
        },

        {
          id: 'information-security-5',
          text: '* registros de auditoria;',
        },

        {
          id: 'information-security-6',
          text: '* proteção de acesso administrativo;',
        },

        {
          id: 'information-security-7',
          text: '* monitoramento de operações relevantes.',
        },

        {
          id: 'information-security-8',
          text: 'Apesar dos esforços empregados, nenhum sistema conectado à internet pode garantir segurança absoluta.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'data-retention',

      title: '6. Retenção dos Dados',

      paragraphs: [
        {
          id: 'data-retention-1',
          text: 'Os dados serão mantidos apenas pelo tempo necessário para:',
        },

        {
          id: 'data-retention-2',
          text: '* prestação dos serviços;',
        },

        {
          id: 'data-retention-3',
          text: '* cumprimento de obrigações legais;',
        },

        {
          id: 'data-retention-4',
          text: '* preservação de registros administrativos;',
        },

        {
          id: 'data-retention-5',
          text: '* resolução de conflitos;',
        },

        {
          id: 'data-retention-6',
          text: '* proteção da plataforma.',
        },

        {
          id: 'data-retention-7',
          text: 'Quando possível e permitido pela legislação, os dados poderão ser excluídos mediante solicitação do usuário.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'user-rights',

      title: '7. Direitos do Usuário',

      paragraphs: [
        {
          id: 'user-rights-1',
          text: 'Nos termos da legislação aplicável, especialmente da Lei Geral de Proteção de Dados (LGPD), o usuário poderá solicitar:',
        },

        {
          id: 'user-rights-2',
          text: '* confirmação da existência de tratamento;',
        },

        {
          id: 'user-rights-3',
          text: '* acesso aos seus dados;',
        },

        {
          id: 'user-rights-4',
          text: '* correção de informações incorretas;',
        },

        {
          id: 'user-rights-5',
          text: '* atualização cadastral;',
        },

        {
          id: 'user-rights-6',
          text: '* exclusão dos dados, quando aplicável;',
        },

        {
          id: 'user-rights-7',
          text: '* informações sobre o tratamento realizado.',
        },

        {
          id: 'user-rights-8',
          text: 'Algumas informações poderão ser mantidas quando houver obrigação legal ou necessidade legítima para proteção da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'cookies-and-similar-technologies',

      title: '8. Cookies e Tecnologias Semelhantes',

      paragraphs: [
        {
          id: 'cookies-and-similar-technologies-1',
          text: 'A Nexora poderá utilizar tecnologias necessárias para manter sessões autenticadas, melhorar a experiência do usuário e garantir o funcionamento adequado da plataforma.',
        },

        {
          id: 'cookies-and-similar-technologies-2',
          text: 'Não utilizamos cookies para venda de dados ou criação de perfis comerciais de terceiros.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'policy-changes',

      title: '9. Alterações desta Política',

      paragraphs: [
        {
          id: 'policy-changes-1',
          text: 'Esta Política poderá ser atualizada sempre que houver mudanças legais, técnicas ou operacionais.',
        },

        {
          id: 'policy-changes-2',
          text: 'Sempre que uma nova versão entrar em vigor, ela será disponibilizada aos usuários.',
        },

        {
          id: 'policy-changes-3',
          text: 'Quando necessário, poderá ser solicitado um novo aceite antes da continuidade da utilização da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'contact',

      title: '10. Contato',

      paragraphs: [
        {
          id: 'contact-1',
          text: 'Em caso de dúvidas sobre esta Política de Privacidade ou sobre o tratamento de dados pessoais, o usuário poderá entrar em contato pelos canais oficiais da Nexora.',
        },
      ] satisfies LegalParagraph[],
    },

    {
      id: 'final-considerations',

      title: 'Considerações Finais',

      paragraphs: [
        {
          id: 'final-considerations-1',
          text: 'A confiança dos nossos usuários é um dos pilares da Nexora.',
        },

        {
          id: 'final-considerations-2',
          text: 'Por isso, buscamos tratar cada informação com responsabilidade, segurança e transparência, mantendo nosso compromisso permanente com a proteção dos dados pessoais e a evolução contínua da plataforma.',
        },
      ] satisfies LegalParagraph[],
    },
  ] satisfies LegalSection[],
};
